import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { useAPI } from '../hooks/useAPI';

interface StorageSummary {
  instant: { count: number; total_gb: number; cost_per_month: number };
  standard: { count: number; total_gb: number; cost_per_month: number };
  archive: { count: number; total_gb: number; cost_per_month: number };
  total_cost: number;
  total_storage_gb: number;
  total_photos: number;
  last_updated: string;
}

const StorageCostWidget: React.FC = () => {
  const { apiCall, loading: authLoading } = useAPI();
  const [summary, setSummary] = useState<StorageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStorageSummary = useCallback(async () => {
    if (authLoading) return;

    try {
      setLoading(true);
      setError(null);
      const data = await apiCall<StorageSummary>('/storage/summary');
      setSummary(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [apiCall, authLoading]);

  useEffect(() => {
    if (!authLoading) {
      fetchStorageSummary();
    }
  }, [authLoading, fetchStorageSummary]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-neutral-200 dark:bg-neutral-700 rounded w-1/3 mb-4"></div>
          <div className="h-32 bg-neutral-200 dark:bg-neutral-700 rounded"></div>
        </div>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">Storage Costs</h3>
        <p className="text-neutral-600 dark:text-neutral-400">Unable to load storage summary</p>
      </div>
    );
  }

  // Prepare data for charts
  const pieData = [
    { name: 'Instant', value: summary.instant.total_gb, color: '#10b981' },
    { name: 'Standard', value: summary.standard.total_gb, color: '#f59e0b' },
    { name: 'Archive', value: summary.archive.total_gb, color: '#3b82f6' }
  ].filter(item => item.value > 0);

  const costData = [
    { tier: 'Instant', cost: summary.instant.cost_per_month, count: summary.instant.count },
    { tier: 'Standard', cost: summary.standard.cost_per_month, count: summary.standard.count },
    { tier: 'Archive', cost: summary.archive.cost_per_month, count: summary.archive.count }
  ].filter(item => item.count > 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-neutral-800 rounded-lg shadow p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Storage Costs</h3>
        <button
          onClick={fetchStorageSummary}
          className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
        >
          Refresh
        </button>
      </div>

      {/* Total Cost - Prominent */}
      <div className="mb-6 text-center">
        <div className="text-4xl font-bold text-neutral-900 dark:text-white">
          ${summary.total_cost.toFixed(2)}
        </div>
        <div className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
          per month • {summary.total_storage_gb.toFixed(1)} GB total
        </div>
      </div>

      {/* Tier Breakdown */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center">
          <div className="text-green-500 text-2xl">⚡</div>
          <div className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mt-1">Instant</div>
          <div className="text-lg font-semibold text-neutral-900 dark:text-white">
            {summary.instant.count}
          </div>
          <div className="text-xs text-neutral-600 dark:text-neutral-400">
            {summary.instant.total_gb.toFixed(1)} GB
          </div>
          <div className="text-xs text-neutral-600 dark:text-neutral-400">
            ${summary.instant.cost_per_month.toFixed(2)}/mo
          </div>
        </div>

        <div className="text-center">
          <div className="text-yellow-500 text-2xl">📦</div>
          <div className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mt-1">Standard</div>
          <div className="text-lg font-semibold text-neutral-900 dark:text-white">
            {summary.standard.count}
          </div>
          <div className="text-xs text-neutral-600 dark:text-neutral-400">
            {summary.standard.total_gb.toFixed(1)} GB
          </div>
          <div className="text-xs text-neutral-600 dark:text-neutral-400">
            ${summary.standard.cost_per_month.toFixed(2)}/mo
          </div>
        </div>

        <div className="text-center">
          <div className="text-blue-500 text-2xl">🗄️</div>
          <div className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mt-1">Archive</div>
          <div className="text-lg font-semibold text-neutral-900 dark:text-white">
            {summary.archive.count}
          </div>
          <div className="text-xs text-neutral-600 dark:text-neutral-400">
            {summary.archive.total_gb.toFixed(1)} GB
          </div>
          <div className="text-xs text-neutral-600 dark:text-neutral-400">
            ${summary.archive.cost_per_month.toFixed(2)}/mo
          </div>
        </div>
      </div>

      {/* Distribution Chart */}
      {pieData.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Storage Distribution</h4>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value: number) => `${value.toFixed(2)} GB`} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Optimization Tip */}
      {summary.instant.count > 100 && (
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            💡 <span className="font-medium">Tip:</span> Move older photos to Archive to save ~
            ${(summary.instant.cost_per_month * 0.95).toFixed(2)}/month
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default StorageCostWidget;
